import styled from 'styled-components';
import { NavLink } from 'react-router-dom';
import { navSections } from './navConfig.js';
import { useRole, usePlatformRole } from '../lib/AuthProvider.js';
import { MerchantSwitcher } from './MerchantSwitcher.js';
import { PartnerStrip } from './PartnerStrip.js';
import { useOnboarding } from '../features/onboarding/hooks.js';
import { STEPS } from '../features/onboarding/steps.js';

/** Sidebar — ports `.sidebar`, `.brand`, `.nav-section`, `.nav-item`, `.active`. */
const Root = styled.nav`
  background: ${({ theme }) => theme.color.kBlack};
  color: #fff;
  padding: 24px 16px;
  display: flex;
  flex-direction: column;

  @media (max-width: 768px) {
    display: none;
  }
`;
const Brand = styled.div`
  font-weight: 900;
  letter-spacing: -0.04em;
  font-size: 20px;
  color: #fff;
  margin-bottom: 4px;
`;
const Sub = styled.div`
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  opacity: 0.5;
  margin-bottom: 32px;
`;
const SectionTitle = styled.div`
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  opacity: 0.4;
  margin: 16px 0 8px;
  padding: 0 8px;
`;
const Item = styled(NavLink)`
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.7);
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 2px;
  &:hover {
    background: rgba(255, 255, 255, 0.06);
    color: #fff;
  }
  &.active {
    background: rgba(255, 255, 255, 0.1);
    color: #fff;
  }
`;
const ItemLabel = styled.span`
  flex: 1;
`;
const ProgressPill = styled.span<{ $done: boolean }>`
  font-size: 10px;
  font-weight: 600;
  padding: 1px 6px;
  border-radius: 10px;
  background: ${({ $done }) => ($done ? 'rgba(72, 199, 142, 0.25)' : 'rgba(255, 255, 255, 0.12)')};
  color: ${({ $done }) => ($done ? '#48c78e' : 'rgba(255, 255, 255, 0.5)')};
  letter-spacing: 0.02em;
  flex-shrink: 0;
`;

export function Sidebar() {
  const role = useRole();
  const platformRole = usePlatformRole();
  const { data: onboardingData } = useOnboarding();

  const completedSteps = onboardingData?.steps.filter((s) => s.status === 'done').length ?? 0;
  const totalSteps = STEPS.length;
  const onboardingDone = completedSteps === totalSteps;

  return (
    <Root>
      <Brand>Kindred</Brand>
      <Sub>Merchant Portal</Sub>
      <MerchantSwitcher />

      {navSections.map((section) => {
        const items = section.items.filter((i) => {
          if (i.platformAdmin) return platformRole === 'kindredAdmin';
          if (i.minRoles) return role != null && i.minRoles.includes(role);
          return true;
        });
        if (items.length === 0) return null;
        return (
          <div key={section.title}>
            <SectionTitle>{section.title}</SectionTitle>
            {items.map((i) => (
              <Item key={i.path} to={i.path}>
                <ItemLabel>{i.label}</ItemLabel>
                {i.path === '/onboarding' && onboardingData && (
                  <ProgressPill $done={onboardingDone}>
                    {completedSteps}/{totalSteps}
                  </ProgressPill>
                )}
              </Item>
            ))}
          </div>
        );
      })}

      <PartnerStrip />
    </Root>
  );
}
